import { useEffect, useMemo } from 'react';

import rfdc from 'rfdc';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import { useAPI } from '../../../../services/api.service';
import { getSavedCardFormSchema } from '../SavedCards/utils';
import { useCheckout } from '../../../../hooks/use-checkout';
import { usePaymentState } from '../../hooks/use-payment-state';
import { useAppConfig } from '../../../../hooks/use-app-config';
import { EPaymentProvider, SavedCard, MixpanelEvents } from '../../../../contracts';
import { SavedCardFormProps, SavedCards } from '../SavedCards/SavedCards';
import { PayButton } from '../PaymentForm/components/PayButton/PayButton';
import { PaymentElementContainer } from '../PaymentElementContainer/PaymentElementContainer';
import { sendMixpanelEvent } from '../../../../utils/mixpanel-utils';

const clone = rfdc();

interface SavedCardProps {
  onReady: () => void;
  onPayClicked: (values: SavedCardFormProps) => void;
  paymentProvider: EPaymentProvider;
  showCardFlow: () => void;
  isSecurityCodeRequired?: boolean;
}

export function SavedCardsContainer({
  onPayClicked,
  onReady,
  showCardFlow,
  paymentProvider,
  isSecurityCodeRequired = true,
}: SavedCardProps) {
  const { API } = useAPI();
  const { checkoutData, getMixpanelEventParams } = useCheckout();
  const savedCards = usePaymentState((state) => state.savedCards);
  const filteredSavedCards = useMemo(
    () => savedCards.filter((card) => card.paymentProvider === paymentProvider),
    [savedCards, paymentProvider],
  );
  const setSavedCards = usePaymentState((state) => state.setSavedCards);
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const setIsApmLoading = usePaymentState((state) => state.setIsApmLoading);
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const { featureFlags } = useAppConfig();
  const shouldHideEmailField = featureFlags?.checkout_enforce_customer_email;

  const formik = useFormik<SavedCardFormProps>({
    initialValues: {
      brand: '',
      cardID: '',
      securityCode: '',
      pspCustomerId: '',
      email: checkoutData?.customer.email || '',
    },
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema: () => getSavedCardFormSchema(isSecurityCodeRequired, shouldHideEmailField),
    onSubmit: () => {},
  });

  useEffect(() => onReady(), [onReady]);

  const onCardRemoveClicked = async (card: SavedCard) => {
    try {
      const clonedSavedCards = clone(savedCards);
      await API.removeSavedCard(card.pspCardIdentifier, paymentProvider);
      const removedCardIndex = clonedSavedCards.findIndex(
        (clonedCard) => clonedCard.pspCardIdentifier === card.pspCardIdentifier,
      );
      clonedSavedCards.splice(removedCardIndex, 1);
      setSavedCards(clonedSavedCards);
      if (card.pspCardIdentifier === formik.values.cardID) {
        formik.setFieldValue('securityCode', '');
        formik.setFieldValue('cardID', '');
        formik.setFieldValue('brand', '');
        formik.setFieldValue('pspCustomerId', '');
      }
      const filteredClonedSavedCards = clonedSavedCards.filter(
        (card) => card.paymentProvider === paymentProvider,
      );
      if (!filteredClonedSavedCards.length) {
        setIsApmLoading(true);
        showCardFlow();
      }

      // Send Mixpanel event for saved card removal
      sendMixpanelEvent(MixpanelEvents.SAVED_CARD_REMOVED, {
        ...getMixpanelEventParams(),
      });
    } catch (error) {
      console.error(error);
    }
  };

  const onAddNewCardClicked = () => {
    setIsApmLoading(true);
    showCardFlow();
  };

  const onPayButtonClicked = async () => {
    formik.submitForm();
    const errors = await formik.validateForm();
    const isFormValid = !Object.keys(errors).length;
    if (!formik.values.cardID) {
      setErrorMessage(t('selectACard'));
    } else if (isFormValid) {
      onPayClicked(formik.values);
    }
  };

  return (
    <PaymentElementContainer
      content={
        <SavedCards
          formik={formik}
          isSecurityCodeRequired={isSecurityCodeRequired}
          cards={filteredSavedCards}
          onAddNewCardClicked={onAddNewCardClicked}
          onCardRemoveClicked={onCardRemoveClicked}
          shouldHideEmailField={shouldHideEmailField}
        />
      }
    >
      <PayButton onClick={onPayButtonClicked} />
    </PaymentElementContainer>
  );
}
